import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { findInitiative, INITIATIVES } from "@/lib/initiatives";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

export default function InitiativeDetail() {
  const { slug } = useParams();
  const it = findInitiative(slug);
  if (!it) return <Navigate to="/initiatives" replace />;
  const Icon = it.icon;
  const others = INITIATIVES.filter((x) => x.slug !== slug).slice(0, 3);

  return (
    <>
      <section className="pt-10 md:pt-16">
        <div className="mx-auto max-w-7xl container-x">
          <Link
            to="/initiatives"
            data-testid="back-to-initiatives"
            className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0A2540]"
          >
            <ArrowLeft className="w-4 h-4" /> All initiatives
          </Link>
        </div>
      </section>

      <section className="section-py">
        <div className="mx-auto max-w-7xl container-x grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ backgroundColor: it.accent }}
              >
                <Icon className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">
                Initiative
              </div>
            </div>
            <h1 className="mt-5 font-serif-display text-4xl md:text-6xl text-[#0A2540] leading-[1.05]">
              {it.title}
            </h1>
            <p className="mt-5 text-lg text-[#4A5568] max-w-2xl">{it.short}</p>
            <div className="mt-8 space-y-5 text-[#1A1A1A] leading-relaxed max-w-2xl">
              {it.long.map((p, i) => (
                <p key={i} className="text-base">
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-10">
              <h3 className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">
                What we do
              </h3>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {it.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <span
                      className="mt-1 rounded-full w-5 h-5 flex items-center justify-center"
                      style={{ backgroundColor: it.accent }}
                    >
                      <Check className="w-3 h-3 text-white" strokeWidth={2.5} />
                    </span>
                    <span className="text-sm text-[#1A1A1A]">{h}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to={`/donate?cause=${encodeURIComponent(it.title)}`}
                data-testid="initiative-donate-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] text-white px-8 py-3.5 font-medium hover:bg-[#D4AF37] hover:text-[#0A2540] transition-colors"
              >
                Support this cause <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/csr"
                className="inline-flex items-center gap-2 rounded-full border border-[#0A2540] text-[#0A2540] px-8 py-3.5 font-medium hover:bg-[#0A2540] hover:text-white transition-colors"
              >
                CSR partnership
              </Link>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[#0A2540]/10">
              <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 grain" />
            </div>
          </div>
        </div>
      </section>

      <section className="section-py bg-white border-t border-[#0A2540]/10">
        <div className="mx-auto max-w-7xl container-x">
          <h3 className="font-serif-display text-3xl text-[#0A2540]">Explore other initiatives</h3>
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link
                to={`/initiatives/${o.slug}`}
                key={o.slug}
                className="group rounded-2xl overflow-hidden border border-[#0A2540]/10 bg-[#F9F8F6]"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={o.image}
                    alt={o.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="font-serif-display text-lg text-[#0A2540] line-clamp-1">
                    {o.title}
                  </div>
                  <div className="mt-2 text-sm text-[#4A5568] line-clamp-2">{o.short}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
